import { PayloadAction } from '@reduxjs/toolkit';
import { PaginatedResponse } from '@dynamika-shared/types/general';
import {
  SiteSubjectDetails,
  SiteSubjectType as BaseSiteSubjectType,
} from '@dynamika-shared/types/subjects';
import { ClinicalTrialType } from '@dynamika-shared/types/clinicalTrials';

// Extended type for external user subjects with additional fields
export type SiteSubjectType = BaseSiteSubjectType & {
  patientCode: string;
  siteId: string;
  cohortName?: string;
  lastUploadedVisitName?: string;
  lastUploadedDate?: string;
};

export type ExternalUserSubjectListResponse = PaginatedResponse<SiteSubjectType>;

export enum ExternalUserFileModuleEnum {
  'DATACOLLECT' = 'DATACOLLECT',
  'EXPORT' = 'EXPORT',
}

export const externalUserFileModuleMap: Record<ExternalUserFileModuleEnum, string> = {
  [ExternalUserFileModuleEnum.DATACOLLECT]: 'Upload',
  [ExternalUserFileModuleEnum.EXPORT]: 'Reading Results',
};

export type ExternalUserFile = {
  id: string;
  siteId: string;
  visitId: string;
  module: ExternalUserFileModuleEnum;
  name: string;
  dataType: string;
  contentType: string;
  size: number;
  uploadItemId?: string;
  created: string;
};

export type SelectedFile = Pick<ExternalUserFile, 'id' | 'name' | 'size'>;

export type FileDownloadInfo = {
  url: string;
  contentType: string;
};

export type Site = {
  id: string;
  name: string;
  code?: string;
};

export type ClinicalTrialContext = {
  clinicalTrialId: string;
  clinicalTrialName?: string;
  sites?: Site[];
};

// New API response types
export type ExternalUserPatientDetails = {
  id: string;
  dob: string;
  status: string;
  code: string; // patient code
  siteName: string;
  cohortName: string;
};

export type ExternalUserVisit = {
  id: string;
  visitDefinitionId: string;
  visitName: string;
  visitStatus: string;
  taskId: string;
  taskStatus: string;
  visitOrder: number;
  visitOrderLocked: boolean;
  canUnlockVisitOrder: boolean;
  visitDate: string; // date format: 2025-12-03
  forecastDate: string;
  assetAcquisitionDate: string;
  edtfAcquisitionDate: string;
  uploadDate: string; // ISO format: 2025-12-03T15:14:49.181Z
};

export type ExternalUserPatientVisitsResponse = {
  clinicalTrialId: string;
  siteId: string;
  patientId: string;
  visits: ExternalUserVisit[];
};

export type ExternalUserSubjectsState = {
  data: Partial<ExternalUserSubjectListResponse> | undefined;
  loading: boolean;
  error?: Error;
  subjectDetails: Partial<SiteSubjectDetails> | undefined;
  search: string;
  selectedSiteFilter: string | null;
  trials: ClinicalTrialType[] | undefined;
  trialsLoading: boolean;
  context: ClinicalTrialContext | undefined;
  contextLoading: boolean;
  selectedTrialId: string | null;
};

type Args = {
  sort?: string[];
  page?: number;
  size?: number;
  patientCode?: string;
  siteId?: string;
};

export type ExternalUserSubjectsListFetchAction = PayloadAction<{
  clinicalTrialId: string;
  args?: Args;
}>;

export type ExternalUserSubjectsListFetchSuccessAction = PayloadAction<
  Partial<ExternalUserSubjectListResponse> | undefined
>;

export type ExternalUserSubjectDetailsFetchAction = PayloadAction<{
  patientId: string;
  clinicalTrialId: string;
}>;

export type ExternalUserSubjectDetailsFetchSuccessAction = PayloadAction<
  Partial<SiteSubjectDetails> | undefined
>;

export type ExternalUserTrialsFetchAction = PayloadAction<{
  role?: string;
} | undefined>;

export type ExternalUserTrialsFetchSuccessAction = PayloadAction<ClinicalTrialType[]>;

export type ExternalUserContextFetchAction = PayloadAction<{
  clinicalTrialId: string;
}>;

export type ExternalUserContextFetchSuccessAction = PayloadAction<ClinicalTrialContext>;
